// Third-party
import React, { useState } from 'react';

// Components
import { CancelButton, CloseButton, PrimaryButton } from '@/views/components/buttons';
import FileUpload from '@/views/containers/admin/admin-portal/FileUpload';
import Modal from '@/views/components/modals/AccessibleModal';

// Types
import User from '@/types/user';
import { DataUploadPortalConfig, DataUploadPortalFile } from '@/types/data-upload-config';
import Locale from '@/types/locale';

// Utils
import { FormattedMessage, formatMessage } from '@/utils/i18n';
import notification from '@/utils/notification';

type StateProps = {
  user?: User | null;
};

type Props = StateProps & {
  config: DataUploadPortalConfig;
  show: boolean;
  locale?: Locale;
  onCancel: () => void;
  onImportFiles: (configKey: string) => void;
};

const FileUploadModal = (props: Props) => {
  const { config, show, locale, onCancel, onImportFiles } = props;

  const [replacedFiles, setReplacedFiles] = useState(config.files.map(() => false));

  const configKey: string = config && config.configKey ? config.configKey : '';
  const files: DataUploadPortalFile[] = config && config.files;

  const handleImportFiles = () => {
    if (!configKey || !files || !files.length) {
      return notification.error(
        formatMessage('Admin.DataUpload.Status.CannotProceed.Description'),
        formatMessage('Admin.DataUpload.Status.CannotProceed.Title'),
      );
    }

    if (replacedFiles.some(replaced => replaced)) {
      return notification.error(
        formatMessage('Admin.DataUpload.Status.CannotProceed.MissingFiles'),
        formatMessage('Admin.DataUpload.Status.CannotProceed.Title'),
      );
    }

    onImportFiles(configKey);
  };

  const handleReplacedFiles = (replaced: boolean, index: number) => {
    replacedFiles[index] = replaced;
    setReplacedFiles([...replacedFiles]);
  };

  return (
    <Modal show={show} onHide={onCancel} className="admin-file-upload-modal" dialogClassName="modal-dialog-centered">
      <Modal.Header>
        <Modal.Title>{config.name}</Modal.Title>
        {!!onCancel && <CloseButton onClick={onCancel} />}
      </Modal.Header>
      <Modal.Body>
        {files.map((file, i) => (
          <FileUpload
            description={file.description}
            configKey={configKey}
            file={file}
            title={file.name}
            key={i}
            onReplaceFile={replaced => handleReplacedFiles(replaced, i)}
          />
        ))}
      </Modal.Body>
      <Modal.Footer className="admin-upload-actions">
        <CancelButton onClick={onCancel} />
        <PrimaryButton onClick={handleImportFiles} className="data-upload-button">
          <FormattedMessage id="Modal.AdminDataUpload.Import" locale={locale} />
        </PrimaryButton>
      </Modal.Footer>
    </Modal>
  );
};

export default FileUploadModal;
